<!--
  src/views/TasksBank.vue
  Master catalog of tasks (course-independent). Admin-area bank screen,
  same shape as your other banks (questions / videos / books).
  Reuses the generic EntityListLayout. Create/edit via DialogAddTask.
-->
<template>
  <div class="TasksBank__View" dir="rtl">
    <EntityListLayout ref="list" :tabs="tabs" namespace="tasksBank">
      <template #title><h1>בנק משימות</h1></template>
            <template v-slot:counter="slotProps">
        סה"כ <b>{{ getTotalRecords(slotProps) }} </b>
      </template>
      <template #button>
        <b-button type="is-primary" icon-left="plus" @click="openTask()">משימה חדשה</b-button>
      </template>
    </EntityListLayout>
  </div>
</template>

<script>
import EntityListLayout from "@/components/EntityListLayout.vue";
import DialogAddTask from "@/components/DialogAddTask.vue";
import DialogLinkTasksToCourse from "@/components/DialogLinkTasksToCourse.vue";
import DialogTasksBankExcel from "@/components/DialogTasksBankExcel.vue";
import TasksService from "@/services/TasksService";
import FieldService from "@/services/FieldService";
import FileService from "@/services/FileService";
import FilterService from "@/services/FilterService";
import { ModalProgrammatic as Modal } from "buefy";

export default {
  name: "TasksBank",
  components: { EntityListLayout },
  computed: {
    tabs() {
      const taskFields = FieldService.list("tasks");
      const taskFilters = FilterService.list();
      return [
        {
          id: "TasksBank",
          title: "כל המשימות",
          isPrimary: true,
          isAdminArea: true,
          fastSearch: true,
          fastSearchOptions: { propName: "Terms", label: "חיפוש בבנק..." },
          checkableRows: true,
          perPage: 500,
          page: 1,
          sortField: "id",
          sortOrder: "desc",
          selectedRows: [],
          getDataAsync: this.getAllTasks,
          sideViewModal: DialogAddTask, // row click -> edit
          // The course filter reuses Query "CourseId"; hide the shared
          // "lessons in selected course" dropdown that string would trigger.
          hideCourseLessonsFilter: true,
          tableActions: [
            { Id: "linkToCourse", Label: "שייך לקורס", icon: "book-plus", click: (rows, refresh) => this.linkToCourse(rows, refresh) },
            { Id: "setActive", Label: "הפוך לפעיל", icon: "check-circle-outline", click: (rows, refresh) => this.setStatus(rows, true, refresh) },
            { Id: "setInactive", Label: "הפוך ללא פעיל", icon: "close-circle-outline", click: (rows, refresh) => this.setStatus(rows, false, refresh) },
          ],
          fields: [
            { ...taskFields.Id, checked: true },
            { ...taskFields.ExternalId, checked: true },
            { ...taskFields.Name, checked: true },
            { ...taskFields.SubjectName, checked: true },
            { ...taskFields.Description, checked: true },
            { ...taskFields.DurationMinutes, checked: true },
            { ...taskFields.Tags, checked: true },
            { ...taskFields.IsActive, checked: true },
          ],
          // Wired to the TasksBankQuery body fields (CourseId / TaskTypeId / SubjectId / Status).
          // Status opens on active tasks only; the admin can clear it to see all.
          filters: [
            { ...taskFilters.BankTaskCourse },
            { ...taskFilters.BankTaskType },
            { ...taskFilters.BankTaskSubject },
            { ...taskFilters.TaskStatus, selected: true },
          ],
          // Renders the "⋯" actions menu (EntityListLayout). Export/Format ignore
          // the tab's current query — they always cover the whole bank, and neither
          // is scoped to a course (the API's optional courseId is never sent).
          excelExport: [
            { title: "ייצוא לאקסל", action: this.exportBank },
            { title: "הורדת תבנית", action: this.downloadFormat },
            { title: "ייבוא מאקסל", action: this.openImport },
          ],
        },
      ];
    },
  },
  methods: {
    getTotalRecords(slotProps) {
      return slotProps.tab && slotProps.tab.total && slotProps.tab.total;
    },
    // Terms / filters (CourseId, TaskTypeId, SubjectId, Status) / paging / sort all flow through
    // from EntityListLayout in `rest`. The table emits the column's field Id as SortedBy
    // (id / name / durationMinutes); map those to the endpoint's accepted keys
    // (id / taskName / duration). Response is PagingResults<TasksBankModel>.
    getAllTasks({ cancelToken, SortedBy, ...rest }) {
      const sortMap = { name: "taskName", durationMinutes: "duration", id: "id" };
      return TasksService.getTasksInBank(
        { ...rest, SortedBy: sortMap[SortedBy] || SortedBy },
        cancelToken && cancelToken.token
      ).then((r) => ({
        data: {
          TotalResults: r.data.totalResults,
          Items: r.data.items || [],
        },
      }));
    },
    // Whole active bank, 44 columns, every row carrying its real task_id — the
    // starting point for the export -> edit -> import round trip.
    exportBank({ loading }) {
      loading(true);
      TasksService.exportTasksBank()
        .then((r) => FileService.saveXlsx(r.data, r.headers, "tasks-bank.xlsx"))
        .catch(() => {
          this.$buefy.toast.open({
            type: "is-danger",
            message: "ייצוא בנק המשימות נכשל.",
            duration: 6000,
          });
        })
        .finally(() => loading(false));
    },
    // Blank template: the 44 headers (required marked *) + the live task-type codes.
    downloadFormat({ loading }) {
      loading(true);
      TasksService.getTasksBankFormat()
        .then((r) => FileService.saveXlsx(r.data, r.headers, "tasks-bank-format.xlsx"))
        .catch(() => {
          this.$buefy.toast.open({
            type: "is-danger",
            message: "הורדת התבנית נכשלה.",
            duration: 6000,
          });
        })
        .finally(() => loading(false));
    },
    openImport() {
      Modal.open({
        component: DialogTasksBankExcel,
        hasModalCard: true,
        trapFocus: true,
        canCancel: ["escape", "outside"],
        props: {
          onSuccess: () => { if (this.$refs.list) this.$refs.list.onNewEntitySave(); },
        },
      });
    },
    // Retiring drops the tasks from the planner feed and from students' plans, so confirm first.
    setStatus(rows, active, refresh) {
      if (!rows || !rows.length) return;
      this.$buefy.dialog.confirm({
        title: active ? "הפיכה לפעיל" : "הפיכה ללא פעיל",
        message: active
          ? `להפוך ${rows.length} משימות לפעילות?`
          : `להפוך ${rows.length} משימות ללא פעילות? הן יוסרו מהתכניות של הסטודנטים.`,
        confirmText: active ? "הפוך לפעיל" : "הפוך ללא פעיל",
        cancelText: "ביטול",
        type: active ? "is-primary" : "is-danger",
        onConfirm: () => {
          TasksService.setTasksStatus(rows.map((r) => r.id), active)
            .then((r) => {
              this.$buefy.toast.open({ type: "is-success", message: r.data.message, duration: 4000 });
              refresh();
            })
            .catch((e) => {
              const msg = e && e.response && e.response.data && e.response.data.error;
              this.$buefy.toast.open({ type: "is-danger", message: msg || "עדכון הסטטוס נכשל.", duration: 6000 });
            });
        },
      });
    },
    linkToCourse(rows, refresh) {
      if (!rows || !rows.length) return;
      Modal.open({
        component: DialogLinkTasksToCourse,
        hasModalCard: true,
        trapFocus: true,
        canCancel: ["escape", "outside"],
        props: {
          tasks: rows,
          onSuccess: refresh,
        },
      });
    },
    openTask(task = null) {
      Modal.open({
        component: DialogAddTask,
        trapFocus: true,
        canCancel: ["escape", "outside"],
        props: {
          store: this.$store,
          row: task,
          onSuccess: () => { if (this.$refs.list) this.$refs.list.onNewEntitySave(); },
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.TasksBank__View { height: 100%; }
</style>
